import { computed } from 'vue';
import { useStore } from 'vuex';
import { useAiChatWidget } from '~/composables/useAiChatWidget';

export const useAiChatClientInfo = () => {
  const store = useStore();
  const { $t } = useNuxtApp();
  const { aiChatEnabled, aiContactFormEnabled } = useAiChatWidget();

  const language = computed(() => store.state.translation.language);
  const languages = computed(() => store.state.translation.languages);
  const organizations = computed(() => store.state.organizations);
  const schoolName = computed(() => store.state.globals.info.school_name);

  const chatConfigurationDefaults = computed(() => ({
    // this should always be sent as markdown for the compiler to work correctly in AI Chat
    welcomeMessage: $t('chatAndContact.drawer.chat.defaultWelcomeMessage', {
      orgName: store.state.globals.info.school_name,
    }),
    bubbleMessage: $t('chatAndContact.drawer.chatBubble.defaultBubbleMessage', {
      orgName: store.state.globals.info.school_name,
    }),
    chatbotName: $t('chatAndContact.drawer.chat.defaultChatbotName'),
    avatarUrl: store.getters.getGlobals.logo?.url || new URL('~/assets/sparkle_avatar_lg.svg', import.meta.url).href,
    color: store.state.globals?.info?.primary_color?.hex || '#662E80',
  }));

  const chatConfiguration = computed(() => {
    const chatConfiguration = store.state.globals.chat_configuration || {};

    return {
      ...chatConfiguration,
      branding: {
        welcome_message: chatConfiguration?.branding?.welcome_message || chatConfigurationDefaults.value.welcomeMessage,
        chatbot_name: chatConfiguration?.branding?.chatbot_name || chatConfigurationDefaults.value.chatbotName,
        photo: { url: chatConfiguration?.branding?.photo?.url || chatConfigurationDefaults.value.avatarUrl },
        color: { hex: chatConfiguration?.branding?.color?.hex || chatConfigurationDefaults.value.color },
        bubble_message: chatConfiguration?.branding?.bubble_message || chatConfigurationDefaults.value.bubbleMessage,
        bubble_message_enabled: chatConfiguration?.branding?.bubble_message_enabled,
        icon_size: chatConfiguration?.branding?.icon_size,
        icon_position: chatConfiguration?.branding?.icon_position,
      },
      // Once custom responses are available, we can use prompts: chatConfiguration?.prompts || [],
      // empty  array just hides the chips for now
      prompts: [],
    };
  });

  const buttonsConfiguration = computed(() => {
    const buttons = [
      {
        name: 'inbox',
        enabled: aiContactFormEnabled.value,
      },
      {
        name: 'ai-chat',
        enabled: aiChatEnabled.value,
      },
    ];
    return {
      enabled: buttons.filter((button) => button.enabled).map((button) => button.name),
    };
  });

  const contactFormTopics = computed(() => {
    const { contact_form_configuration } = store.state.globals;
    const { topics, default_email } = contact_form_configuration;
    const defaultTopics = [
      { id: 0, category: 'General Inquiry' },
      { id: 1, category: 'Technical Support' },
      { id: 2, category: 'Feedback' },
    ];

    return topics?.length
      ? topics.map((topic: any, index: number) => ({
          id: index,
          category: topic.category,
          inbox: default_email,
        }))
      : defaultTopics;
  });

  const contactFormConfiguration = computed(() => {
    const { contact_form_configuration } = store.state.globals;
    const cleanedContactConfig = { ...contact_form_configuration };
    cleanedContactConfig.topics = contactFormTopics.value;

    return cleanedContactConfig;
  });

  const clientInfo = computed(() => {
    return {
      organizations: organizations.value,
      topics: contactFormTopics.value,
      chatConfiguration: chatConfiguration.value,
      contactFormConfiguration: contactFormConfiguration.value,
      buttonsConfiguration: buttonsConfiguration.value,
      language: language.value,
      languages: languages.value,
      currentOrgName: schoolName.value,
    };
  });

  return {
    chatConfiguration,
    contactFormConfiguration,
    buttonsConfiguration,
    clientInfo,
  };
};
